import z from 'zod'
import {
  firestoreWriteTimestamp,
  nullSafeOptionalFirestoreTimestamp,
  nullSafeOptionalString,
  nullSafeOptionalStringArray,
  nullSafeString,
} from '../shared/schema'
import { RubricType } from '../../types'

const rubricTypeSchema = z.nativeEnum(RubricType)

// Per-rubric weighting + role applicability. The shape encodes which roles a
// rubric applies to, so each role's applicable weights can be validated to sum
// to 100 independently:
//   { allRoles: 40 }                            → applies to everyone, weight 40
//   { byRole: { <roleId>: 60, <roleId>: 30 } }  → applies only to those roleIds
// Role applicability is derived from this field — there is no separate
// assignmentRoles array. Keys are roleIds, matching the values in
// roomState.assignmentRoles ({ userId: roleId }).
// Both members are strict() so a payload carrying BOTH keys (or any extra
// key) is rejected rather than silently coerced to one shape. This keeps the
// role-applicability encoding unambiguous: a document matches exactly one shape.
const weightsSchema = z.union([
  z.object({ allRoles: z.number().min(0).max(100) }).strict(),
  z
    .object({
      byRole: z.record(z.string(), z.number().min(0).max(100)),
    })
    .strict(),
])

export const schema = z.object({
  // Copy of SlideRubric fields, denormalised so the assignment's rubric is
  // self-contained for both grade calculation (TypeScript) and AI evaluation
  // (Go). Go's model.Rubric reads rubric/rubricDescription/rubricPrompt/
  // rubricType/slideId/startSlideId; role applicability is derived from
  // `weights` (Go reads it too in custom mode), replacing assignmentRoles.
  rubric: nullSafeString(''),
  rubricDescription: nullSafeString(''),
  rubricPrompt: nullSafeString(''),
  rubricType: z.preprocess((val) => val ?? RubricType.blooms, rubricTypeSchema),
  /**
   * Copy of the SlideRubric's per-score grading criteria (index = score),
   * pinned here so learningObjective rubrics grade against the criteria the
   * assignment was configured with. Empty for other types.
   */
  rubricScoreCriteria: nullSafeOptionalStringArray([]),
  startSlideId: nullSafeOptionalString(),
  /** acts as end slide if startSlideId is present */
  slideId: nullSafeOptionalString(),

  // Per-role weighting + applicability (see weightsSchema above). Each role's
  // applicable weights must sum to 100 (enforced at the UI and function layer,
  // not the schema).
  weights: weightsSchema.default({ allRoles: 0 }),

  // Score → percentage mapping.
  // Keys are string representations of rubric score integers ("0".."4").
  // Values are percentages 0–100.
  // Example: { "4": 100, "3": 85, "2": 70, "1": 50, "0": 0 }
  scoreMapping: z.record(z.string(), z.number().min(0).max(100)).optional(),

  updatedAt: nullSafeOptionalFirestoreTimestamp(),
})

export const empty = Object.freeze(
  schema.parse({
    rubric: '',
    rubricDescription: '',
    rubricPrompt: '',
    rubricType: RubricType.blooms,
  })
)

export const writeSchema = schema.extend({
  updatedAt: firestoreWriteTimestamp,
})

export type FirestoreAssignmentRubric = z.infer<typeof schema>
export type FirestoreAssignmentRubricWrite = z.infer<typeof writeSchema>
